import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import React, { Component } from 'react';
import { BIRTH_YEAR, BIRTH_DAY, BIRTH_MONTH } from './constants';
import { DateOfBirthPickerView } from './DateOfBirthPickerView';

dayjs.extend(objectSupport);
dayjs.extend(customParseFormat);

export const DATE_FORMAT = 'YYYY-MM-DD';

const isValidDateObj = (dateObj) => {
    const date = dayjs(dateObj);

    return (
        date.isValid() &&
        date.get('year') === parseInt(dateObj['year'], 10) &&
        date.get('month') === parseInt(dateObj['month'], 10) &&
        date.get('date') === parseInt(dateObj['date'], 10) &&
        !date.isBefore(dayjs('1-1-1900')) &&
        date.isAfter(dayjs().subtract(200, 'year'))
    );
};

/**
 * @param {number} ageRestriction
 * @param {Object} day
 * @param {boolean} isYelp
 * @param {Object} month
 * @param {function(date: string): void} onDateChange
 * @param {function(): void} onDayFocus
 * @param {function(): void} onMonthFocus
 * @param {function(): void} onYearFocus
 * @param {boolean} showInvalid
 * @param {Object} year
 * @param {string} overrideErrorMessage
 * @param {boolean} shouldUseMezzanineColors
 * @return {JSX.Element}
 */
class DateOfBirthPickerContainer extends Component {
    static defaultProps = {
        overrideErrorMessage: null,
        shouldUseMezzanineColors: false,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.showInvalid !== prevState.cachedShowInvalid) {
            return {
                cachedShowInvalid: nextProps.showInvalid,
                invalidMessage: nextProps.showInvalid
                    ? 'Please type a valid date of birth.'
                    : undefined,
            };
        }

        return null;
    }

    constructor(props) {
        super(props);

        this.state = {
            birthDay: props.defaultBirthDay || '',
            birthMonth: props.defaultBirthMonth || '',
            birthYear: props.defaultBirthYear || '',
            cachedShowInvalid: props.showInvalid,
            focus: null,
            invalidMessage: props.showInvalid
                ? 'Please type a valid date of birth.'
                : undefined,
        };
    }

    render() {
        const { birthMonth, birthDay, birthYear, focus, invalidMessage } =
            this.state;
        const { shouldUseMezzanineColors } = this.props;

        const day = Object.assign({}, this.props.day, {
            onBlur: this.handleBlur,
            onChange: (value) => this.handleBirthDayChange(value.target.value),
            onFocus: this.handleDayFocus,
            value: birthDay,
        });
        const month = Object.assign({}, this.props.month, {
            onBlur: this.handleBlur,
            onChange: (value) =>
                this.handleBirthMonthChange(value.target.value),
            onFocus: this.handleMonthFocus,
            value: birthMonth,
        });
        const year = Object.assign({}, this.props.year, {
            onBlur: this.handleBlur,
            onChange: (value) => this.handleBirthYearChange(value.target.value),
            onFocus: this.handleYearFocus,
            value: birthYear,
        });

        let displayError = invalidMessage;

        if (birthMonth === '' || birthDay === '' || birthYear === '') {
            displayError = undefined;
        }

        if (this.props.showInvalid && invalidMessage) {
            displayError = 'Please type a valid date of birth.';
        }

        if (focus !== null) {
            displayError = undefined;
        }

        if (this.props.overrideErrorMessage) {
            displayError = this.props.overrideErrorMessage;
        }

        return (
            <DateOfBirthPickerView
                day={day}
                focusOn={focus}
                invalidMessage={displayError}
                month={month}
                year={year}
                shouldUseMezzanineColors={shouldUseMezzanineColors}
            />
        );
    }

    handleDayFocus = () => {
        const { onDayFocus } = this.props;

        this.setState({
            focus: BIRTH_DAY,
        });
        onDayFocus && onDayFocus();
    };

    handleMonthFocus = () => {
        const { onMonthFocus } = this.props;

        this.setState({
            focus: BIRTH_MONTH,
        });
        onMonthFocus && onMonthFocus();
    };

    handleYearFocus = () => {
        const { onYearFocus } = this.props;
        this.setState({
            focus: BIRTH_YEAR,
        });
        onYearFocus && onYearFocus();
    };

    handleBlur = () => {
        this.setState({
            focus: null,
        });
    };

    handleBirthMonthChange(value) {
        const month = value ? value.slice(0, 2) : value;

        const { onDateChange } = this.props;
        const { message, date } = this.validateDate('birthMonth', month);

        if (value) {
            this.setState({
                birthMonth: month,
                invalidMessage: message,
            });
        } else {
            this.setState({
                birthMonth: '',
                invalidMessage: message,
            });
        }

        onDateChange(date);
    }

    handleBirthDayChange(value) {
        const day = value ? value.slice(0, 2) : value;

        const { onDateChange } = this.props;
        const { date, message } = this.validateDate('birthDay', day);

        if (value) {
            this.setState({
                birthDay: day,
                invalidMessage: message,
            });
        } else {
            this.setState({
                birthDay: '',
                invalidMessage: message,
            });
        }

        onDateChange(date);
    }

    handleBirthYearChange(value) {
        const year = value ? value.slice(0, 4) : value;

        const { onDateChange } = this.props;

        const { date, message } = this.validateDate('birthYear', year);

        if (value) {
            this.setState({
                birthYear: year,
                invalidMessage: message,
            });
        } else {
            this.setState({
                birthYear: '',
                invalidMessage: message,
            });
        }

        onDateChange(date);
    }

    canProcessDate(birthYear, birthMonth, birthDay) {
        return birthYear && birthMonth && birthDay;
    }

    validateDate(changedField, value) {
        const { ageRestriction, isYelp } = this.props;

        const updatedState = Object.assign({}, this.state);
        updatedState[changedField] = value;

        const { birthYear, birthMonth, birthDay } = updatedState;

        if (!this.canProcessDate(birthYear, birthMonth, birthDay)) {
            return {
                message: undefined,
                date: null,
            };
        }

        const dateObj = {
            year: birthYear,
            month: birthMonth - 1,
            date: birthDay,
        };

        const attemptingDate = dayjs({
            year: birthYear,
            month: birthMonth - 1,
            date: birthDay,
        });
        const isValidDate =
            isValidDateObj(dateObj) && !attemptingDate.isAfter(dayjs());

        if (ageRestriction) {
            if (isValidDate) {
                const oldEnough = dayjs().subtract(ageRestriction, 'years');

                if (attemptingDate.diff(oldEnough) >= 0) {
                    /*
                        We don't want the error message to show up in the DoB field if it's Yelp; should
                        show up by Save and Continue button. See maybeRenderButtonErrorText() function
                        in CreateAccountFormView.js
                    */
                    return isYelp
                        ? {
                              message: undefined,
                              date: null,
                          }
                        : {
                              message:
                                  'Oops! You must be 18 to sign up for Zocdoc. Minors can be added to your account later.',
                              date: null,
                          };
                } else {
                    //do not include timezone with birthdate or else it could be recorded incorrectly
                    return { date: attemptingDate.format(DATE_FORMAT) };
                }
            }
            return {
                message: 'Please type a valid date of birth.',
                date: null,
            };
        }

        if (!isValidDate) {
            return {
                message: 'Please type a valid date of birth.',
                date: null,
            };
        } else {
            //do not include timezone with birthdate or else it could be recorded incorrectly
            return { date: attemptingDate.format(DATE_FORMAT) };
        }
    }
}

export { DateOfBirthPickerContainer, DateOfBirthPickerContainer as default };
